'use client';

import { flattenCategoryTree } from '@/features/categories/categoryTreeFilters';
import {
  isShopPath,
  setShopFocusIntent,
  SHOP_FOCUS_FILTER_BAR,
  SHOP_FOCUS_RESULTS,
} from '@/features/shop/shopFocusIntent';
import { buildShopPath } from '@/features/shop/shopQuery';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Form, Offcanvas } from 'react-bootstrap';
import { FaTimes } from 'react-icons/fa';

import CategoryTreeMap from './CategoryTreeMap.jsx';

const BREAKPOINTS = {
  sm: 596,
  md: 768,
  lg: 992,
};

const SUGGESTED_CATEGORY_SLUGS = [
  'tablettes-durcies',
  'accessoires',
  'moniteur',
  'tv-video-et-home-cinema',
  'tapis-de-souris-gaming',
  'storage',
  'xbox-series-x-et-s',
  'cables',
  'consoles',
  'playstation-5',
  'playstation-4',
  'console-portable',
  'claviers-gaming',
  'audio-video',
  'cartes-graphiques',
  'memoire',
  'ssd',
  'refroidissement-et-ventilateurs',
  'supports',
  'realite-virtuelle',
  'jeux-video',
  'chaises',
  'enceintes',
  'ordinateurs',
  'ordinateurs-portables-gaming',
  'pc-gaming',
  'mini-pc',
  'processeurs',
  'accessoires-gaming-pc',
  'ecrans-gaming',
  'mobilier-gaming',
  'refroidisseurs',
  'xbox-one',
  'nintendo-switch',
  'accessoires-xbox-one',
  'manettes',
  'playstation',
  'jeux-playstation',
  'casques-vr',
  'disques-durs-externes',
  'tvs-qled',
  'accessories',
  'laptop',
  'pc',
  'phone',
  'desktop',
  'legacy-systems',
  'all-in-ones',
  'gaming',
  'monitors',
  'video-games',
  'controllers',
  'racing-wheels',
  'xbox-series-x-and-s',
  'headsets',
  'virtual-reality',
  'smartphones',
  'tablets',
  'graphics-cards',
  'towers',
  'oled-tvs',
  'software',
  'appliances',
  'fans-and-cooling',
  'led-and-lcd-tvs',
  'computers',
  'mini-computers',
  'desks',
  'cooling-pads',
  'gamepads',
  'vr-headsets',
  'external-hard-drives',
];

function getCategoryLimit(width) {
  if (width < BREAKPOINTS.sm) return 15;
  if (width < BREAKPOINTS.md) return 20;
  if (width < BREAKPOINTS.lg) return 25;
  return 30;
}

function filterTree(nodes, query) {
  if (!query) {
    return nodes;
  }

  const lower = query.toLowerCase();

  return (nodes || [])
    .map((node) => {
      const children = node.children ? filterTree(node.children, query) : [];

      if (
        String(node.name || '')
          .toLowerCase()
          .includes(lower) ||
        children.length > 0
      ) {
        return { ...node, children };
      }

      return null;
    })
    .filter(Boolean);
}

export default function CategoryFilter({
  categoriesTree = [],
  pathname = '',
  onNavigate,
  onCategorySelect,
  showHeadline = true,
  showTopCategories = true,
  triggerLabel = 'Show All',
  triggerVariant = 'link',
  triggerClassName = '',
  singleSelect = false,
  filterMessages = {},
}) {
  const fm = (key, fallback) =>
    typeof filterMessages[key] === 'string' ? filterMessages[key] : fallback;
  const [showTree, setShowTree] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewportWidth, setViewportWidth] = useState(1200);
  const router = useRouter();
  const currentPathname = pathname || usePathname() || '/';
  const searchParams = useSearchParams();
  const selectedSlugs = useMemo(
    () => Array.from(new Set(searchParams.getAll('category').filter(Boolean))),
    [searchParams],
  );

  useEffect(() => {
    const updateViewportWidth = () =>
      setViewportWidth(window.innerWidth || 1200);

    updateViewportWidth();
    window.addEventListener('resize', updateViewportWidth);
    window.addEventListener('orientationchange', updateViewportWidth);

    return () => {
      window.removeEventListener('resize', updateViewportWidth);
      window.removeEventListener('orientationchange', updateViewportWidth);
    };
  }, []);

  const topCategories = useMemo(() => {
    const flattened = flattenCategoryTree(categoriesTree || []);
    const suggested = SUGGESTED_CATEGORY_SLUGS.map((slug) =>
      flattened.find((category) => category.slug === slug),
    ).filter(Boolean);

    return (suggested.length > 0 ? suggested : flattened).slice(
      0,
      getCategoryLimit(viewportWidth),
    );
  }, [categoriesTree, viewportWidth]);

  const filteredCategories = useMemo(
    () => filterTree(categoriesTree, searchTerm),
    [categoriesTree, searchTerm],
  );

  const navigateTo = useCallback(
    (updates, focusTarget) => {
      if (isShopPath(currentPathname) && focusTarget) {
        setShopFocusIntent(focusTarget);
      }

      router.push(buildShopPath(searchParams, updates, currentPathname), {
        scroll: false,
      });
      onNavigate?.();
    },
    [currentPathname, onNavigate, router, searchParams],
  );

  const toggleCategory = (slug) => {
    if (singleSelect) {
      const isRemovingCategory = selectedSlugs.includes(slug);
      const nextCategory = selectedSlugs.includes(slug) ? undefined : slug;

      navigateTo(
        {
          category: nextCategory,
          page: 1,
        },
        isRemovingCategory ? SHOP_FOCUS_FILTER_BAR : SHOP_FOCUS_RESULTS,
      );

      return;
    }

    const current = new Set(selectedSlugs);
    const isRemovingCategory = current.has(slug);

    if (current.has(slug)) {
      current.delete(slug);
    } else {
      current.add(slug);
    }

    navigateTo(
      {
        category: current.size ? Array.from(current) : undefined,
        page: 1,
      },
      isRemovingCategory ? SHOP_FOCUS_FILTER_BAR : SHOP_FOCUS_RESULTS,
    );
  };

  const handleSelectCategory = (category) => {
    if (onCategorySelect) {
      onCategorySelect(category);
      setShowTree(false);
      setSearchTerm('');
      return;
    }

    toggleCategory(category.slug);
    setShowTree(false);
    setSearchTerm('');
  };

  return (
    <section className="category-filter">
      {showHeadline ? (
        <hgroup className="mb-3">
          <h5 className="toggle-categories text-info d-flex justify-content-between align-items-center gap-2">
            <span role="button" onClick={() => setShowTree(!showTree)}>
              <span aria-hidden="true" className="me-1">
                ⏷
              </span>
              <span className="fs-6 fs-msm-5 fw-medium">
                {fm('headline', 'Filter by Category')}
              </span>
            </span>
            <Button
              variant="link"
              size="sm"
              className="text-nowrap px-0 shadow-none d-flex align-items-center gap-1 text-decoration-none mt-1 text-info"
              onClick={() => setShowTree(true)}
            >
              <span aria-hidden="true">☰</span>
              <span className="fs-7 fs-msm-6">{fm('showAll', 'Show All')}</span>
            </Button>
          </h5>
        </hgroup>
      ) : (
        <Button
          size="sm"
          variant={triggerVariant}
          className={triggerClassName}
          onClick={() => setShowTree(true)}
        >
          <span aria-hidden="true">☰</span>
          <span>{triggerLabel}</span>
        </Button>
      )}

      {showTopCategories ? (
        <div className="category-badges d-flex flex-wrap gap-2 mb-2">
          {topCategories.map((category) => (
            <Badge
              key={category.slug}
              pill
              bg={
                selectedSlugs.includes(category.slug) ? 'primary' : 'secondary'
              }
              className="clickable-badge"
              onClick={() => toggleCategory(category.slug)}
            >
              {category.name}
            </Badge>
          ))}
        </div>
      ) : null}

      <Offcanvas
        show={showTree}
        onHide={() => {
          setShowTree(false);
          setSearchTerm('');
        }}
        placement="end"
        className="category-tree-offcanvas w-85 w-sm-auto bg-landscape-surface-2"
        container={typeof document !== 'undefined' ? document.body : undefined}
        backdrop
        backdropClassName="category-tree-backdrop"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title className="text-primary flex-grow-1">
            {fm('offcanvasTitle', 'All Categories')}
            <div className="offcanvas-search mt-3">
              <div className="search-input-group rounded-pill overflow-hidden shadow flex-nowrap w-100">
                <div className="input-control flex-grow-1 d-flex justify-content-between align-items-center gap-1">
                  <Form.Control
                    type="text"
                    name="q"
                    placeholder={fm(
                      'searchPlaceholder',
                      'Search categories...',
                    )}
                    spellCheck="false"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    className="search-input"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSearchTerm('')}
                    title={fm('clearSearch', 'Clear')}
                    aria-label={fm('clearSearch', 'Clear')}
                    className={`clear-search-icon border-start-0 shadow-none${
                      searchTerm ? ' clear-search-icon--visible' : ''
                    }`}
                    aria-hidden={!searchTerm}
                  >
                    <FaTimes size={20} />
                  </Button>
                </div>
              </div>
            </div>
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <CategoryTreeMap
            categories={filteredCategories}
            fullTree={categoriesTree}
            onSelect={handleSelectCategory}
            searchTerm={searchTerm}
          />
        </Offcanvas.Body>
      </Offcanvas>
    </section>
  );
}
